import {
  ceilingYAt,
  floorYAt,
  type WavesChart,
  type WavesObstacle,
  type WavesTerrain,
  type WavesTerrainWall,
} from '../../wavesChart.js';
import { step, type WavesEngineInput } from './core.js';
import { makePlannerState } from './headlessRunnerPlanSegment.js';
import { type WavesEngineConfig } from './state.js';

export interface CentreRange {
  min: number;
  max: number;
}

/**
 * Float-safety margin: planner targets Y values at least this far
 * from a gate's hard edge so accumulated floating-point error in the
 * engine's piecewise-linear integration doesn't push the player's
 * bounding circle across the edge by a fraction of a pixel.
 *
 * The engine uses strict `<` / `>` comparisons in `gateCollides`, so
 * Y on the edge wouldn't collide if the math were exact — but the
 * engine integrates via repeated dt accumulations, and a 10-segment
 * trajectory can drift ~0.1 px from the planner's closed-form Y.
 * 1px margin keeps every planned obstacle solidly inside the band.
 *
 * The chart-builder filter uses the same margin so it never accepts
 * obstacles the perfect runner would reject; otherwise builtin maps
 * could ship with single-pixel infeasibilities that no human can clear.
 */
export const PLANNER_EDGE_MARGIN = 1;

/**
 * Required Y range for a player's centre at the obstacle's beat.
 * Includes the player's bounding radius so the path-planner targets
 * a feasible centre rather than a "circle straddling the edge" Y,
 * plus a small float-safety margin. Returns null for obstacles whose
 * feasible range is empty (the validator forbids this but the planner
 * is defensive).
 *
 * Saws are circular blades the generator mounts flush against one
 * corridor surface (see `injectSaws` in `wavesFromCircles.ts`), so
 * exactly one side is open. A single `CentreRange` can't express the
 * disjoint above/below regions of a free-floating saw, and the
 * single-flip-per-segment planner only threads a contiguous band — so
 * we resolve the saw to the side that actually has corridor room
 * (the wider band when, hypothetically, both sides are open). Because
 * the band is clamped to the LOCAL corridor here, the generator's
 * reachability filter and the perfect-replay planner stay in lockstep
 * (both call this with the same terrain) — "filter accepts" ⇔
 * "planner clears".
 */
export function feasibleCentreRange(
  obs: WavesObstacle,
  playerRadius: number,
  terrain: WavesTerrain,
): CentreRange | null {
  if (obs.kind === 'gate') {
    const min = obs.gapTopY + playerRadius + PLANNER_EDGE_MARGIN;
    const max = obs.gapBottomY - playerRadius - PLANNER_EDGE_MARGIN;
    if (max < min) {
      return null;
    }
    return { min, max };
  }
  const corridorMin = ceilingYAt(terrain, obs.beat) + playerRadius;
  const corridorMax = floorYAt(terrain, obs.beat) - playerRadius;
  const above: CentreRange = {
    min: corridorMin,
    max: Math.min(obs.centerY - obs.radius - playerRadius - PLANNER_EDGE_MARGIN, corridorMax),
  };
  const below: CentreRange = {
    min: Math.max(obs.centerY + obs.radius + playerRadius + PLANNER_EDGE_MARGIN, corridorMin),
    max: corridorMax,
  };
  const aboveOk = above.max >= above.min;
  const belowOk = below.max >= below.min;
  if (aboveOk && belowOk) {
    return above.max - above.min >= below.max - below.min ? above : below;
  }
  if (aboveOk) {
    return above;
  }
  if (belowOk) {
    return below;
  }
  return null;
}

/**
 * Required Y range for the player's centre to CLEAR a lethal wall at
 * its beat — treated as a planner waypoint so the single-flip-per-
 * segment planner climbs above (floor wall) or dives below (ceiling
 * wall) the 90-degree face in time. A floor wall demands the body stay
 * above `wall.top`; a ceiling wall demands it stay below `wall.bottom`.
 * The opposite corridor surface bounds the other side. Returns null if
 * the clear band is empty (the corridor-width invariant prevents this
 * for valid charts, but the planner stays defensive).
 */
export function feasibleWallRange(
  wall: WavesTerrainWall,
  terrain: WavesTerrain,
  playerRadius: number,
): CentreRange | null {
  let min: number;
  let max: number;
  if (wall.surface === 'floor') {
    min = ceilingYAt(terrain, wall.beat) + playerRadius + PLANNER_EDGE_MARGIN;
    max = wall.top - playerRadius - PLANNER_EDGE_MARGIN;
  } else {
    min = wall.bottom + playerRadius + PLANNER_EDGE_MARGIN;
    max = floorYAt(terrain, wall.beat) - playerRadius - PLANNER_EDGE_MARGIN;
  }
  if (max < min) {
    return null;
  }
  return { min, max };
}

/**
 * Reachable Y range at `toBeat` starting from `(y, pressing)` at
 * `fromBeat`. Integrates a handful of single-flip trajectories
 * through the actual engine so corridor clamping and edge handling
 * match the runtime behaviour exactly.
 */
export function reachableYAtBeat(
  config: WavesEngineConfig,
  chart: WavesChart,
  fromBeat: number,
  toBeat: number,
  y: number,
  pressing: boolean,
): CentreRange {
  const dt = toBeat - fromBeat;
  if (dt <= 1e-9) {
    return { min: y, max: y };
  }
  const samples: number[] = [y];
  for (let i = 0; i <= 12; i += 1) {
    const flipBeat = fromBeat + (dt * i) / 12;
    for (const flipToPress of [true, false]) {
      if (i === 0 && flipToPress === pressing) {
        continue;
      }
      const state = makePlannerState(chart, fromBeat, y, pressing);
      const inputs: WavesEngineInput[] = [
        { beat: flipBeat, type: flipToPress ? 'down' : 'up' },
      ];
      const out = step({
        config,
        state,
        fromBeat,
        toBeat,
        inputs,
      });
      if (out.state.alive) {
        samples.push(out.state.playerY);
      }
    }
  }
  const noFlip = makePlannerState(chart, fromBeat, y, pressing);
  const held = step({
    config,
    state: noFlip,
    fromBeat,
    toBeat,
    inputs: [],
  });
  if (held.state.alive) {
    samples.push(held.state.playerY);
  }
  return { min: Math.min(...samples), max: Math.max(...samples) };
}

export function intersectRanges(
  a: CentreRange,
  b: CentreRange,
): CentreRange | null {
  const min = Math.max(a.min, b.min);
  const max = Math.min(a.max, b.max);
  if (max < min) {
    return null;
  }
  return { min, max };
}

export interface BeatBandGroup<T> {
  beat: number;
  band: CentreRange;
  items: T[];
}

/**
 * Sort beat/band waypoints and collapse entries sharing a beat (within
 * `epsilon`) into a single group whose band is the intersection of the
 * members'. When two same-beat bands don't overlap the later entry is
 * dropped (returned in `dropped`) so the surviving group stays threadable.
 *
 * Both the perfect-replay planner (`buildBeatGroups`) and the chart-
 * builder's `filterUnreachableObstacles` group hazards through here, so
 * they always target the SAME Y per beat — the lockstep the "filter
 * accepts ⇔ planner clears" guarantee depends on. Keeping this in one
 * place is why the two passes can't silently drift apart.
 */
export function groupHazardsByBeat<T>(
  entries: ReadonlyArray<{ beat: number; band: CentreRange; item: T }>,
  epsilon = 1e-6,
): { groups: BeatBandGroup<T>[]; dropped: T[] } {
  const sorted = [...entries].sort((a, b) => a.beat - b.beat);
  const groups: BeatBandGroup<T>[] = [];
  const dropped: T[] = [];
  for (const entry of sorted) {
    const last = groups[groups.length - 1];
    if (last && Math.abs(last.beat - entry.beat) < epsilon) {
      const merged = intersectRanges(last.band, entry.band);
      if (!merged) {
        dropped.push(entry.item);
        continue;
      }
      last.band = merged;
      last.items.push(entry.item);
      continue;
    }
    groups.push({ beat: entry.beat, band: entry.band, items: [entry.item] });
  }
  return { groups, dropped };
}
